// EventDetail.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  AvatarGroup,
} from "@mui/material";
import {
  Close,
  Edit,
  CalendarToday,
  AccessTime,
  Description,
  Person,
  Delete,
  Public,
} from "@mui/icons-material";
import {
  EVENT_TYPE_LABELS,
  EVENT_TYPE_COLORS,
} from "../../utils/models/calendarModel";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";

const EventDetail = ({ event, onEdit, onClose }) => {
  const theme = useTheme();
  const { update, remove } = useAuditFirestore("calendarEvents");
  const { data: users } = useCollection("users");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!event) return null;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // ELIMINAR PERMANENTEMENTE de Firestore
      await remove(event.id);
      onClose();
    } catch (error) {
      console.error("Error al eliminar evento:", error);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Obtener nombres de usuarios asignados
  const getAssignedUsers = () => {
    // Verificar que assignedTo existe y es un array
    if (
      !event.assignedTo ||
      !Array.isArray(event.assignedTo) ||
      event.assignedTo.length === 0
    )
      return [];
    return event.assignedTo
      .map((uid) => users?.find((u) => u.id === uid))
      .filter(Boolean);
  };

  const assignedUsers = getAssignedUsers();
  const isGeneralEvent = !event.assignedTo || !Array.isArray(event.assignedTo) || event.assignedTo.length === 0;

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: { xs: "16px", sm: "24px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flex: 1, minWidth: 0 }}
          >
            <Box
              sx={{
                width: { xs: 8, sm: 12 },
                height: { xs: 32, sm: 40 },
                borderRadius: "6px",
                backgroundColor:
                  event.color || EVENT_TYPE_COLORS[event.eventType],
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" }, wordBreak: "break-word" }}>
                {event.title}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                <Chip
                  label={EVENT_TYPE_LABELS[event.eventType]}
                  size="small"
                  sx={{
                    backgroundColor:
                      event.color || EVENT_TYPE_COLORS[event.eventType],
                    color: "white",
                    fontWeight: 600,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                    height: { xs: 24, sm: 28 },
                  }}
                />
                {isGeneralEvent ? (
                  <Chip
                    icon={<Public sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                    label="Evento general"
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      height: { xs: 24, sm: 28 },
                    }}
                  />
                ) : (
                  <Chip
                    label={`${assignedUsers.length} asignado(s)`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: theme.palette.info.main,
                      color: theme.palette.info.main,
                      fontWeight: 500,
                      fontSize: { xs: "0.65rem", sm: "0.75rem" },
                      height: { xs: 24, sm: 28 },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ ml: 1 }}>
            <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </Box>

        <List dense disablePadding>
          {event.allDay ? (
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                <CalendarToday
                  fontSize="small"
                  sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Todo el día"
                secondary={formatDate(event.startDate)}
                primaryTypographyProps={{ variant: "body2", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                secondaryTypographyProps={{ variant: "caption", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
              />
            </ListItem>
          ) : (
            <>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                  <CalendarToday
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Inicio"
                  secondary={`${formatDate(event.startDate)} a las ${formatTime(event.startDate)}`}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  secondaryTypographyProps={{ variant: "caption", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                  <AccessTime
                    fontSize="small"
                    sx={{ color: theme.palette.warning.main, fontSize: { xs: 18, sm: 20 } }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Fin"
                  secondary={`${formatDate(event.endDate)} a las ${formatTime(event.endDate)}`}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                  secondaryTypographyProps={{ variant: "caption", fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
                />
              </ListItem>
            </>
          )}

          {event.description && (
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                <Description
                  fontSize="small"
                  sx={{ color: theme.palette.info.main, fontSize: { xs: 18, sm: 20 } }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Descripción"
                secondary={event.description}
                primaryTypographyProps={{ variant: "body2", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                secondaryTypographyProps={{ variant: "body2", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              />
            </ListItem>
          )}

          {event.createdByName && (
            <ListItem sx={{ px: 0 }}>
              <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 } }}>
                <Person
                  fontSize="small"
                  sx={{ color: theme.palette.success.main, fontSize: { xs: 18, sm: 20 } }}
                />
              </ListItemIcon>
              <ListItemText
                primary="Creado por"
                secondary={event.createdByName}
                primaryTypographyProps={{ variant: "body2", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                secondaryTypographyProps={{ variant: "body2", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              />
            </ListItem>
          )}

          {/* Sección de asignados */}
          <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
            <ListItemIcon sx={{ minWidth: { xs: 30, sm: 36 }, mt: 1 }}>
              {isGeneralEvent ? (
                <Public
                  fontSize="small"
                  sx={{ color: theme.palette.primary.main, fontSize: { xs: 18, sm: 20 } }}
                />
              ) : (
                <Person
                  fontSize="small"
                  sx={{ color: theme.palette.secondary.main, fontSize: { xs: 18, sm: 20 } }}
                />
              )}
            </ListItemIcon>
            <ListItemText
              primary="Asignado a"
              primaryTypographyProps={{ variant: "body2", fontWeight: 600, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              secondary={
                isGeneralEvent ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                  >
                    Evento general - Visible para todos los usuarios
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    <AvatarGroup max={4} sx={{ justifyContent: "flex-start" }}>
                      {assignedUsers.map((u) => (
                        <Avatar
                          key={u.id}
                          alt={u.displayName || u.email}
                          src={u.photoURL}
                          sx={{
                            width: { xs: 28, sm: 32 },
                            height: { xs: 28, sm: 32 },
                            fontSize: { xs: "0.7rem", sm: "0.8rem" },
                            bgcolor: theme.palette.primary.main,
                          }}
                        >
                          {(u.displayName || u.email)?.charAt(0).toUpperCase()}
                        </Avatar>
                      ))}
                    </AvatarGroup>
                    <Box sx={{ mt: 0.5 }}>
                      {assignedUsers.map((u) => (
                        <Typography
                          key={u.id}
                          variant="caption"
                          display="block"
                          color="text.secondary"
                          sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                        >
                          {u.displayName || u.email}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )
              }
              secondaryTypographyProps={{ component: "div" }}
            />
          </ListItem>
        </List>

        <Divider sx={{ mt: { xs: 2, sm: 3 }, mb: 2 }} />

        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              "&:hover": {
                borderColor: theme.palette.error.dark,
                backgroundColor: theme.palette.error.main + "10",
              },
            }}
          >
            Eliminar
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ borderRadius: "12px", textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            >
              Cerrar
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => {
                onClose();
                onEdit(event);
              }}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: "none",
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
              }}
            >
              Editar
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Diálogo de confirmación para eliminar */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: { xs: "16px", sm: "20px" },
            padding: 1,
            margin: { xs: 2, sm: 3 },
            width: "100%",
            maxWidth: { xs: "95vw", sm: "500px" },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: { xs: "1rem", sm: "1.25rem" } }}>Eliminar evento</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
            ¿Estás seguro de que deseas eliminar el evento "{event.title}"? Esta
            acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deleting}
            sx={{ borderRadius: "10px", textTransform: "none", fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
            fullWidth
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={<Delete />}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              boxShadow: "none",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
            }}
            fullWidth
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EventDetail;